import {
  MgState,
  UserEntityInterface,
  UserQueryInterface,
  UserMetadataResponseInterface,
} from '@types';
import { createSelector } from 'reselect';
import _isEmpty from 'lodash/isEmpty';
import { FeaturePermission, PagePermission, PermissionActions } from '@enums';
import { userHasPermissions } from '@utils';

const getMetadata = (store: MgState): UserMetadataResponseInterface => store?.user.metadata;

export const hasUnsentAnswersSelector = createSelector(
  [getMetadata],
  (metadata) => metadata?.hasUnsentAnswers
);

export const lastAnswerIdSelector = createSelector(
  [getMetadata],
  (metadata) => metadata?.lastAnswerId
);

export const lastQuestionnaireIdSelector = createSelector(
  [getMetadata],
  (metadata) => metadata?.lastQuestionnaireId
);

export const usersFiltersSelector = (store: MgState): Partial<UserQueryInterface> =>
  store?.user?.usersFilters;

export const currentUserSelector = (store: MgState): UserEntityInterface =>
  store?.user?.currentUser;

export const currentUserRoleSelector = createSelector([currentUserSelector], (currentUser) => {
  if (!_isEmpty(currentUser?.roles)) {
    return currentUser?.roles[0];
  } else {
    return !_isEmpty(currentUser?.unit?.roles) ? currentUser?.unit?.roles[0] : null;
  }
});

export const usersSelector = (store: MgState): UserEntityInterface[] => store?.user?.users;

export const usersTotalSelector = (store: MgState): number => store?.user?.total;

export const usersPageNumberSelector = createSelector([usersFiltersSelector], (filters) => {
  return filters?.offset / filters?.limit;
});

export const usersRowsPerPageSelector = createSelector(
  [usersFiltersSelector],
  (filters) => filters?.limit
);

export const usersTotalPagesSelector = createSelector(
  [usersFiltersSelector, usersTotalSelector],
  (filters, total) => Math.ceil(total / filters?.limit)
);

export const isUserFromBYOrganization = createSelector(
  [currentUserSelector],
  (currentUser) => currentUser.organization?.id === 1
);

export const hasFeaturePermissionsSelectorFactory = (
  page: PagePermission,
  features: FeaturePermission[],
  actions: PermissionActions[]
) =>
  createSelector([currentUserSelector], (currentUser) =>
    userHasPermissions(currentUser, page, features, actions)
  );

export const canUpdateUserRoleSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Management,
      [FeaturePermission.ManagementUsers],
      [PermissionActions.Update]
    ),
  ],
  (canUpdateUserRole) => canUpdateUserRole
);
